import React, { ReactNode } from 'react';
import { ReactComponent as CloseIcon } from '@/img/x-gray.svg';
import './UIModal.scss';

export interface UIModalProps {
  /**
   * Чи відкрите модальне вікно
   */
  isOpen?: boolean;

  /**
   * Обробник закриття модального вікна
   */
  onClose?: () => void;

  /**
   * Заголовок модального вікна
   */
  title?: string;

  /**
   * Підзаголовок модального вікна (наприклад "Required fields *")
   */
  subtitle?: string;

  /**
   * Контент модального вікна
   */
  children?: ReactNode;

  /**
   * Розмір модального вікна
   */
  size?: 'small' | 'medium' | 'large' | 'xlarge' | 'custom';

  /**
   * Показувати весь заголовок (title + close button + divider)
   */
  showHeader?: boolean;

  /**
   * Показувати кнопку закриття
   */
  showCloseButton?: boolean;

  /**
   * Кастомна ширина (тільки для size="custom")
   */
  customWidth?: string;

  /**
   * Кастомна висота (тільки для size="custom")
   */
  customHeight?: string;

  /**
   * Кастомна максимальна ширина (тільки для size="custom")
   */
  customMaxWidth?: string;

  /**
   * Кастомна максимальна висота (тільки для size="custom")
   */
  customMaxHeight?: string;

  /**
   * Закривати по кліку на backdrop
   */
  closeOnBackdropClick?: boolean;

  /**
   * Закривати по Escape
   */
  closeOnEscape?: boolean;

  /**
   * Додатковий CSS клас
   */
  className?: string;
}

const baseClass = 'UIModal';

const UIModal: React.FC<UIModalProps> = ({
                                           isOpen = false,
                                           onClose = () => {},
                                           title,
                                           subtitle,
                                           children,
                                           size = 'medium',
                                           showHeader = true,
                                           showCloseButton = true,
                                           customWidth,
                                           customHeight,
                                           customMaxWidth,
                                           customMaxHeight,
                                           closeOnBackdropClick = true,
                                           closeOnEscape = true,
                                           className = '',
                                         }) => {
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (closeOnEscape && e.key === 'Escape') {
      onClose();
    }
  };

  const containerClasses = [
    baseClass,
    isOpen && `${baseClass}--open`,
    className,
  ].filter(Boolean).join(' ');

  const contentClasses = [
    `${baseClass}__content`,
    `${baseClass}__content--${size}`,
  ].filter(Boolean).join(' ');

  // Кастомні стилі для size="custom"
  const customStyles: React.CSSProperties = {};
  if (size === 'custom') {
    if (customWidth) customStyles.width = customWidth;
    if (customHeight) customStyles.height = customHeight;
    if (customMaxWidth) customStyles.maxWidth = customMaxWidth;
    if (customMaxHeight) customStyles.maxHeight = customMaxHeight;
  }

  // Логіка показу заголовку
  const shouldShowHeader = showHeader && (title || showCloseButton);

  const renderTextWithAsterisks = (text: string) => {
    const parts = text.split('*');
    return parts.map((part, index) => (
      <React.Fragment key={index}>
        {part}
        {index < parts.length - 1 && <span className={`${baseClass}__asterisk`}>*</span>}
      </React.Fragment>
    ));
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={containerClasses}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className={contentClasses} style={customStyles}>
        {/* Заголовок */}
        {shouldShowHeader && (
          <div className={`${baseClass}__header`}>
            <div className={`${baseClass}__title-container`}>
              {title && (
                <div className={`${baseClass}__title-section`}>
                  <h2 className={`${baseClass}__title`}>
                    {renderTextWithAsterisks(title)}
                  </h2>
                  {subtitle && (
                    <div className={`${baseClass}__subtitle`}>
                      {renderTextWithAsterisks(subtitle)}
                    </div>
                  )}
                </div>
              )}

              {showCloseButton && (
                <button
                  className={`${baseClass}__close-button`}
                  onClick={onClose}
                  aria-label="Close modal"
                >
                  <CloseIcon />
                </button>
              )}
            </div>

            <hr className={`${baseClass}__divider`} />
          </div>
        )}

        {/* Основний контент */}
        <div className={`${baseClass}__body`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default UIModal;
